import { Switch, Route, Redirect } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/not-found";
import Home from "@/pages/home";
import Blog from "@/pages/blog";
import BlogCategory from "@/pages/blog-category";
import BlogPostPage from "@/pages/blog-post";
import Admin from "@/pages/admin";
import AuthPage from "@/pages/auth";
import Community from "@/pages/community";
import CommunityForum from "@/pages/community-forum";
import CommunityCalendar from "@/pages/community-calendar";
import CommunityResources from "@/pages/community-resources";
import Podcast from "@/pages/podcast";
import Investors from "@/pages/investors";
import Accelerators from "@/pages/accelerators";
import CloudCredits from "@/pages/cloud-credits";
import CompanyRegistrations from "@/pages/company-registrations";
import TrademarkRegistrations from "@/pages/trademark-registrations";
import PatentRegistrations from "@/pages/patent-registrations";
import BookPreOrder from "@/pages/book";
import Calculators from "@/pages/calculators";
import CalculatorPage from "@/pages/calculator-page";
import Terms from "@/pages/terms";
import Privacy from "@/pages/privacy";
import Disclaimer from "@/pages/disclaimer";
import ProfilePage from "@/pages/profile";
import Accessibility from "@/pages/accessibility";
import UserAnalytics from "@/pages/user-analytics";
import AdminLandingPages from "@/pages/admin-landing-pages";
import ForgotPassword from "@/pages/forgot-password";
import ResetPassword from "@/pages/reset-password";
import Booking from "@/pages/booking";
import SaasPositioningAdvisor from "@/pages/saas-positioning-advisor";
import { DocumentMeta } from "@/components/DocumentMeta";

function Router() {
  return (
    <>
      <DocumentMeta />
      <Switch>
      <Route path="/" component={Home} />
      <Route path="/blog" component={Blog} />
      <Route path="/blog/:slug">{(params) => {
        const categorySlugs = ["startup-finance", "growth-metrics", "market-research", "founder-strategy"];
        if (categorySlugs.includes(params.slug)) {
          return <BlogCategory />;
        }
        return <BlogPostPage />;
      }}</Route>
      <Route path="/podcast" component={Podcast} />
      <Route path="/investors">
        <Redirect to="/investor-directory" />
      </Route>
      <Route path="/investor-directory" component={Investors} />
      <Route path="/accelerators" component={Accelerators} />
      <Route path="/cloud-credits" component={CloudCredits} />
      <Route path="/company-registrations" component={CompanyRegistrations} />
      <Route path="/trademark-registrations" component={TrademarkRegistrations} />
      <Route path="/patent-registrations" component={PatentRegistrations} />
      <Route path="/book" component={BookPreOrder} />
      <Route path="/calculators" component={Calculators} />
      <Route path="/calculators/:slug" component={CalculatorPage} />
      <Route path="/terms" component={Terms} />
      <Route path="/privacy" component={Privacy} />
      <Route path="/disclaimer" component={Disclaimer} />
      <Route path="/admin" component={Admin} />
      <Route path="/login" component={AuthPage} />
      <Route path="/community" component={Community} />
      <Route path="/community/forum" component={CommunityForum} />
      <Route path="/community/calendar" component={CommunityCalendar} />
      <Route path="/community/resources" component={CommunityResources} />
      <Route path="/profile" component={ProfilePage} />
      <Route path="/accessibility" component={Accessibility} />
      <Route path="/admin/users" component={UserAnalytics} />
      <Route path="/admin/landing-pages" component={AdminLandingPages} />
      <Route path="/forgot-password" component={ForgotPassword} />
      <Route path="/reset-password" component={ResetPassword} />
      <Route path="/booking" component={Booking} />
      {/* Hidden from Navbar by design — entry points are organic search, sitemap.xml, and direct links. */}
      <Route path="/saas-positioning-advisor" component={SaasPositioningAdvisor} />
      <Route component={NotFound} />
    </Switch>
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <div className="min-h-screen bg-background text-foreground selection:bg-primary selection:text-primary-foreground">
          <a href="#main-content" className="skip-to-main" data-testid="link-skip-to-main">
            Skip to main content
          </a>
          <Toaster />
          <Router />
        </div>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
